import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // Fetch users
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/auth/users');
      setUsers(response.data.data);
      setFilteredUsers(response.data.data);
    } catch (err) {
      setError('Error al cargar usuarios: ' + (err.response?.data?.message || err.message));
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter users by search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/auth/users/${userId}`);
      setUsers(users.filter(u => u._id !== userId));
      setDeleteConfirm(null);
      alert('Usuario eliminado exitosamente');
    } catch (err) {
      alert('Error al eliminar usuario: ' + (err.response?.data?.message || err.message));
      console.error('Error deleting user:', err);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'administrador':
        return 'badge-admin';
      case 'medico':
        return 'badge-doctor';
      case 'recepcionista':
        return 'badge-receptionist';
      case 'paciente':
        return 'badge-patient';
      default:
        return '';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'administrador':
        return 'Administrador';
      case 'medico':
        return 'Médico';
      case 'recepcionista':
        return 'Recepcionista';
      case 'paciente':
        return 'Paciente';
      default:
        return role;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="users-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>Gestión de Usuarios</h1>
            <p>Administrar usuarios del sistema</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
            Volver al Dashboard
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="card">
          <div className="search-section">
            <input
              type="text"
              placeholder="Buscar por nombre, email o rol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {loading ? (
            <div className="loading">Cargando usuarios...</div>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Teléfono</th>
                    <th>Estado</th>
                    <th>Fecha de Registro</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center' }}>
                        No se encontraron usuarios
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <strong>{user.firstName} {user.lastName}</strong>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td>{user.phone || '-'}</td>
                        <td>
                          <span className={user.isActive ? 'status-active' : 'status-inactive'}>
                            {user.isActive ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          {user._id === currentUser._id ? (
                            <span className="text-muted">Tu cuenta</span>
                          ) : user.role === 'administrador' ? (
                            <span className="text-muted">Admin</span>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(user)}
                              className="btn btn-danger btn-sm"
                            >
                              Eliminar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Eliminación</h2>
              <button onClick={() => setDeleteConfirm(null)} className="modal-close">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>¿Está seguro que desea eliminar al usuario?</p>
              <div className="user-info">
                <p><strong>Nombre:</strong> {deleteConfirm.firstName} {deleteConfirm.lastName}</p>
                <p><strong>Email:</strong> {deleteConfirm.email}</p>
                <p><strong>Rol:</strong> {getRoleLabel(deleteConfirm.role)}</p>
              </div>
              <p className="warning-text">Esta acción no se puede deshacer.</p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setDeleteConfirm(null)} className="btn btn-secondary">
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm._id)}
                className="btn btn-danger"
              >
                Eliminar Usuario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
